import { computed, h, type ComputedRef, type VNodeChild } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import type { DropdownDividerOption, DropdownGroupOption, DropdownOption, DropdownRenderOption } from 'naive-ui'
import { useLintNotesStore } from '../stores/lint_notes'
import { useSynthesisSuggestionsStore } from '../stores/synthesis_suggestions'
import { useAgentStore } from '../stores/agent'
import { useAgentHealthStore } from '../stores/agent_health'

// naive-ui's `<NDropdown :options>` accepts a union of option shapes;
// the umbrella alias isn't exported from the public package entry, so
// we rebuild it from the four arms exported individually. Exported so
// WikiHeader can widen our precisely-typed menus to it at the single
// `<NDropdown :options>` binding — the one seam where MenuItem/MenuGroup
// must be bridged to naive-ui's option union.
export type DropdownMixedOption = DropdownOption | DropdownGroupOption | DropdownDividerOption | DropdownRenderOption

// Owns the WikiHeader's navigation-menu state: the typed key vocabulary,
// the admin submenu (used both as the desktop Admin dropdown's options
// and as the children of the mobile hamburger's Admin entry), the
// mobile-menu wrapper, the render-label function, and the dispatcher
// that maps a selected key onto the right action.
//
// Lives as a composable rather than inside WikiHeader.vue so the file
// stays under the 300-line cap and so the menu logic is testable
// without mounting the whole header. The caller (WikiHeader) passes
// the non-router actions it wants the dispatcher to invoke — `Ask`
// and `Sign out` flip parent emits / store actions only WikiHeader
// has the wiring for, so they enter as injected callbacks.

export type MenuKey =
  | 'threads'
  | 'new-page'
  | 'lint'
  | 'synthesis'
  | 'agent'
  | 'output-skills'
  | 'usage'
  | 'memory'
  | 'retrieval-bench'
  | 'tokens'
  | 'shares'
  | 'expert-consultations'
  | 'calendar'
  | 'cv'
  | 'agent-repos'
  | 'agent-prompts'
  | 'channels'
  | 'host-monitoring'
  | 'product-owner-runs'
  | 'reminders'
  | 'lists'
  | 'jobs'
  | 'sign-out'

// The menu-item shape we build and expose: a narrow, MenuKey-typed
// option literal with optional children and href. We keep the menus
// typed as `MenuItem[]` / `MenuGroup[]` all the way through the
// composable so `key: MenuKey` is enforced at construction and flows
// into onMenuSelect's exhaustive switch — rather than erasing it to
// `string` behind the old `DropdownMixedOption[]` return cast.
// The widening to naive-ui's option union happens once, at WikiHeader's
// `<NDropdown :options>` binding.
export interface MenuItem {
  label: string
  key: MenuKey
  href?: string
  children?: Array<MenuItem | MenuGroup>
}

// A non-selectable section heading in the admin dropdown. naive-ui
// renders `type: 'group'` options as a label above their `children`; the
// group itself isn't clickable, so its `key` never reaches `onMenuSelect`.
export interface MenuGroup {
  type: 'group'
  label: string
  key: string
  children: MenuItem[]
}

export interface HeaderMenuActions {
  onThreads: () => void
  onSignOut: () => void
  isAdmin: ComputedRef<boolean>
}

export interface HeaderMenu {
  // Kept precisely typed (not widened to a DropdownMixedOption[] cast) so
  // the `key: MenuKey` invariant survives to every consumer: adding an
  // item with a key outside MenuKey is a compile error at the literal,
  // which in turn trips onMenuSelect's exhaustive switch.
  // WikiHeader widens these to DropdownMixedOption[] at its one
  // `<NDropdown :options>` binding.
  adminSubmenuItems: ComputedRef<MenuGroup[]>
  mobileMenu: ComputedRef<MenuItem[]>
  // Param stays naive-ui's wide `DropdownOption` — that's the type
  // `<NDropdown :render-label>` actually invokes it with at runtime;
  // every such option is one of ours, so we narrow internally.
  renderMenuOption: (option: DropdownOption) => VNodeChild
  onMenuSelect: (key: MenuKey) => void
}

export function useHeaderMenu(actions: HeaderMenuActions): HeaderMenu {
  const router = useRouter()
  const lintNotes = useLintNotesStore()
  const synthesisSuggestions = useSynthesisSuggestionsStore()
  const agentStore = useAgentStore()
  const { openCount: lintOpenCount } = storeToRefs(lintNotes)
  const { openCount: synthesisOpenCount } = storeToRefs(synthesisSuggestions)
  const { sessions: agentSessions } = storeToRefs(agentStore)
  const { activityLabel: productOwnerLabel } = storeToRefs(useAgentHealthStore())

  const lintLabel = computed(() => (lintOpenCount.value > 0 ? `Lint (${lintOpenCount.value})` : 'Lint'))
  const synthesisLabel = computed(() =>
    synthesisOpenCount.value > 0 ? `Synthesis (${synthesisOpenCount.value})` : 'Synthesis',
  )
  const runningSessionCount = computed(() => agentSessions.value.filter((s) => s.running).length)
  const agentLabel = computed(() =>
    runningSessionCount.value > 0 ? `Agent sessions (${runningSessionCount.value})` : 'Agent sessions',
  )

  // Admin submenu — used by the desktop Admin dropdown. Grouped by concern:
  // Review (queues), Create (authoring), System (operational).
  const adminSubmenuItems = computed<MenuGroup[]>(() => [
    {
      type: 'group',
      label: 'Review',
      key: 'grp-review',
      children: [
        { label: lintLabel.value, key: 'lint' },
        { label: synthesisLabel.value, key: 'synthesis' },
      ],
    },
    { type: 'group', label: 'Create', key: 'grp-create', children: [{ label: 'New page', key: 'new-page' }] },
    {
      type: 'group',
      label: 'System',
      key: 'grp-system',
      children: [
        { label: 'Agent', key: 'agent' },
        { label: 'Output skills', key: 'output-skills' },
        { label: 'Usage', key: 'usage' },
        { label: 'Memory', key: 'memory' },
        { label: 'Retrieval bench', key: 'retrieval-bench' },
        { label: 'API tokens', key: 'tokens' },
        { label: 'Shares', key: 'shares' },
        { label: 'Expert consultations', key: 'expert-consultations' },
        { label: 'Calendar', key: 'calendar' },
        { label: 'Live CV', key: 'cv' },
        { label: 'Agent repos', key: 'agent-repos' },
        { label: 'Prompt library', key: 'agent-prompts' },
        { label: 'Agent channels', key: 'channels' },
        { label: 'Host monitoring', key: 'host-monitoring' },
        { label: productOwnerLabel.value, key: 'product-owner-runs' },
        { label: 'Reminders', key: 'reminders' },
        { label: 'Lists', key: 'lists' },
        { label: 'Jobs — mission control', key: 'jobs', href: '/jobs/' },
      ],
    },
  ])

  // Mobile hamburger: flat single-level list. Agent sessions
  // leads (most-used); Ask sits at the bottom (rarely used). No nested
  // Admin submenu — every item is directly reachable in one tap.
  const mobileMenu = computed<MenuItem[]>(() => {
    const items: MenuItem[] = []
    if (actions.isAdmin.value) {
      items.push({ label: agentLabel.value, key: 'agent' })
      items.push({ label: lintLabel.value, key: 'lint' })
      items.push({ label: synthesisLabel.value, key: 'synthesis' })
      items.push({ label: 'New page', key: 'new-page' })
      items.push({ label: 'Output skills', key: 'output-skills' })
      items.push({ label: 'Usage', key: 'usage' })
      items.push({ label: 'Memory', key: 'memory' })
      items.push({ label: 'Retrieval bench', key: 'retrieval-bench' })
      items.push({ label: 'API tokens', key: 'tokens' })
      items.push({ label: 'Shares', key: 'shares' })
      items.push({ label: 'Expert consultations', key: 'expert-consultations' })
      items.push({ label: 'Calendar', key: 'calendar' })
      items.push({ label: 'Live CV', key: 'cv' })
      items.push({ label: 'Agent repos', key: 'agent-repos' })
      items.push({ label: 'Prompt library', key: 'agent-prompts' })
      items.push({ label: 'Agent channels', key: 'channels' })
      items.push({ label: 'Host monitoring', key: 'host-monitoring' })
      items.push({ label: productOwnerLabel.value, key: 'product-owner-runs' })
      items.push({ label: 'Reminders', key: 'reminders' })
      items.push({ label: 'Lists', key: 'lists' })
      items.push({ label: 'Jobs — mission control', key: 'jobs', href: '/jobs/' })
    }
    items.push({ label: 'Ask', key: 'threads' })
    items.push({ label: 'Sign out', key: 'sign-out' })
    return items
  })

  // naive-ui's `render-label` contract is widest-type; we narrow at the
  // boundary because every option reaching this function comes from
  // one of the computed menus above, and we need `href` to render the
  // Jobs link as a real anchor. Parent options (the `Admin` group)
  // just show their label — the submenu chevron is added by NDropdown.
  function renderMenuOption(option: DropdownOption): VNodeChild {
    const item = option as MenuItem
    if (item.href) {
      return h('a', { href: item.href, target: '_blank', rel: 'noopener', class: 'block no-underline' }, item.label)
    }
    return item.label
  }

  function onMenuSelect(key: MenuKey): void {
    switch (key) {
      case 'threads':
        actions.onThreads()
        break
      case 'new-page':
        router.push({ name: 'wikiPageNew' })
        break
      case 'lint':
        router.push('/lint')
        break
      case 'synthesis':
        router.push('/synthesis')
        break
      case 'agent':
        router.push('/agent')
        break
      case 'output-skills':
        router.push('/output-skills')
        break
      case 'usage':
        router.push('/usage')
        break
      case 'memory':
        router.push('/memory')
        break
      case 'retrieval-bench':
        router.push('/admin/retrieval-bench')
        break
      case 'tokens':
        router.push('/admin/tokens')
        break
      case 'shares':
        router.push('/admin/shares')
        break
      case 'expert-consultations':
        router.push('/admin/expert-consultations')
        break
      case 'calendar':
        router.push('/admin/calendar')
        break
      case 'cv':
        router.push('/admin/cv')
        break
      case 'agent-repos':
        router.push('/admin/agent-repos')
        break
      case 'agent-prompts':
        router.push('/admin/agent-prompts')
        break
      case 'channels':
        router.push('/channels')
        break
      case 'host-monitoring':
        router.push('/admin/host-stats')
        break
      case 'product-owner-runs':
        router.push('/admin/product-owner-runs')
        break
      case 'reminders':
        router.push('/reminders')
        break
      case 'lists':
        router.push('/lists')
        break
      case 'sign-out':
        actions.onSignOut()
        break
      case 'jobs':
        /* handled by the <a> renderer above */ break
      default: {
        // Exhaustiveness guard: if a MenuKey arm is added without a case
        // here, `key` is no longer `never` and this fails to compile —
        // so a new menu item can't silently become a no-op.
        const _exhaustive: never = key
        return _exhaustive
      }
    }
  }

  // Return the computeds at their precise MenuGroup[]/MenuItem[] types —
  // no `DropdownMixedOption[]` cast erasing `key: MenuKey` here. WikiHeader
  // widens to naive-ui's union at its single `<NDropdown :options>`
  // binding, so the invariant holds everywhere up to that one seam.
  return {
    adminSubmenuItems,
    mobileMenu,
    renderMenuOption,
    onMenuSelect,
  }
}
